import cn from 'clsx';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ColorPalette } from '../../../types';
import { IconType } from '../../../types';
import Icon from '../../Icon';

import s from './CheckBox.module.scss';

export interface CheckboxChangeEventTarget {
  value?: any;
  checked: boolean;
}
export interface CheckBoxChangeEvent {
  target: CheckboxChangeEventTarget;
  stopPropagation: () => void;
  preventDefault: () => void;
}

type CheckBoxChangeEventHandler = (e: CheckBoxChangeEvent) => void;

export interface CheckBoxProps {
  id?: HTMLInputElement['id'];
  label?: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: CheckBoxChangeEventHandler;
  children?: React.ReactNode;
  checkType?: 'mark' | 'box';
}

const CheckBox: FC<React.PropsWithChildren<React.PropsWithChildren<CheckBoxProps>>> = ({
  label = null,
  disabled = false,
  defaultChecked = false,
  checkType = 'box',
  ...restProps
}) => {
  const { onChange, ...checkBoxRestProps } = restProps;

  const [isChecked, setChecked] = useState(defaultChecked);

  useEffect(() => {
    setChecked(defaultChecked);
  }, [defaultChecked]);

  const checkBoxOnChange = useCallback(
    (e: any) => {
      if (onChange) onChange(e);
      setChecked(e.target.checked);
    },
    [onChange]
  );

  const icon: IconType = useMemo(() => {
    if (checkType === 'box') {
      if (isChecked) {
        return 'check-solid';
      }
      return 'check-line';
    }
    return 'check-mark';
  }, [isChecked, checkType]);

  const color: ColorPalette = useMemo(() => {
    if (isChecked) {
      return 'violet.600';
    }
    return 'gray.300';
  }, [isChecked]);

  return (
    <label className={cn(s.label, disabled ? s.disabled : '')}>
      <Icon color={color} type={icon} size={28} />
      <input disabled={disabled} type="checkbox" className={s.checkbox} checked={isChecked} onChange={checkBoxOnChange} {...checkBoxRestProps} />
      {label && <span>{label}</span>}
    </label>
  );
};

export default CheckBox;
